import { CSSProperties, useRef, useEffect, useState } from 'react';
import { FloatingCharacter as FloatingCharacterType } from '../types';
import './FloatingCharacter.css';

interface FloatingCharacterProps extends FloatingCharacterType {
  mousePosition: { x: number; y: number };
}

export default function FloatingCharacter({
  src,
  alt,
  className,
  mousePosition
}: FloatingCharacterProps) {
  const characterRef = useRef<HTMLImageElement>(null);
  const [motion, setMotion] = useState({ x: 0, y: 0, rotate: 0, scale: 1 });

  useEffect(() => {
    if (!characterRef.current) return;

    const rect = characterRef.current.getBoundingClientRect();
    const characterX = rect.left + rect.width / 2;
    const characterY = rect.top + rect.height / 2 + window.scrollY;

    const dx = mousePosition.x - characterX;
    const dy = mousePosition.y - characterY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const threshold = 200;
    if (distance < threshold && mousePosition.x !== 0 && distance > 0) {
      const moveScale = (threshold - distance) / threshold;
      const strength = 72;
      const moveX = (-dx / distance) * moveScale * strength;
      const moveY = (-dy / distance) * moveScale * strength;
      const rotate = Math.max(-10, Math.min(10, moveX * 0.12));
      const scale = 1 + moveScale * 0.08;

      setMotion({ x: moveX, y: moveY, rotate, scale });
    } else {
      setMotion({ x: 0, y: 0, rotate: 0, scale: 1 });
    }
  }, [mousePosition]);

  const motionStyle = {
    '--offset-x': `${motion.x}px`,
    '--offset-y': `${motion.y}px`,
    '--float-rotate': `${motion.rotate}deg`,
    '--float-scale': motion.scale
  } as CSSProperties;

  return (
    <div className={`floating-character-shell ${className}`} style={motionStyle} aria-hidden="true">
      <img
        ref={characterRef}
        src={src}
        alt={alt}
        className="floating-character"
        loading="lazy"
      />
    </div>
  );
}
